import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium",
    "transition-[background-color,border-color,color,opacity,transform] duration-fast ease-out",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal",
    "active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-45 disabled:active:translate-y-0",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        /* The primary action. Ink on paper, everywhere on the site. */
        default: "bg-ink text-ink-inverse hover:bg-ink-hover",
        /* The signal action. Reserved for the moment of purchase. */
        signal: "bg-signal text-signal-foreground hover:bg-signal-hover",
        outline:
          "border border-line-strong bg-surface text-ink hover:border-ink hover:bg-surface-2",
        secondary: "bg-surface-2 text-ink hover:bg-surface-3",
        ghost: "text-ink-muted hover:bg-surface-2 hover:text-ink",
        /* For placing on the band (Section tone="ink"). */
        inverse: "bg-band-ink text-band hover:bg-band-ink/85",
        destructive: "bg-danger text-danger-foreground hover:opacity-90",
        link: "h-auto p-0 text-ink underline decoration-line-strong underline-offset-4 hover:decoration-ink",
      },
      size: {
        default: "h-11 px-5 text-body",
        sm: "h-9 px-3.5 text-small",
        lg: "h-13 px-7 text-body-lg",
        icon: "size-11",
        "icon-sm": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
